// League calendars use Turkey time, never the player's browser timezone.
const DAY = 24 * 60 * 60 * 1000;
const ISTANBUL_OFFSET = 3 * 60 * 60 * 1000;

export interface OfferWeekLeague {
  // eslint-disable-next-line camelcase
  pazar_aksami_teklif_acik?: boolean;
  // eslint-disable-next-line camelcase
  guncelleme_beklemeden_teklif_acik?: boolean;
  // eslint-disable-next-line camelcase
  haftalik_guncelleme_zamani?: unknown;
  // eslint-disable-next-line camelcase
  bas_tarih?: unknown;
  // eslint-disable-next-line camelcase
  bit_tarih?: unknown;
}

export function timestampMillis(value: any): number {
  if (value instanceof Date) return value.getTime();
  if (typeof value?.toMillis === 'function') return value.toMillis();
  if (typeof value?.seconds === 'number') return value.seconds * 1000;
  if (typeof value === 'number') return value < 1e12 ? value * 1000 : value;
  return NaN;
}

/** Takvim gününün İstanbul'daki başlangıcı (ms). */
const turkeyDayStart = (time: number) => {
  const local = new Date(time + ISTANBUL_OFFSET);
  return Date.UTC(local.getUTCFullYear(), local.getUTCMonth(), local.getUTCDate()) - ISTANBUL_OFFSET;
};

/**
 * Sezon aralığı (25 Ekim 2026 kullanıcı kararı: maç teklifleri yalnız sezon tarihleri içinde).
 * Başlangıç gününün 00:00'ından bitiş gününün sonuna kadar (İstanbul); tarih yoksa o uç sınırsız.
 */
export function leagueSeason(league?: OfferWeekLeague | null) {
  const start = timestampMillis(league?.bas_tarih);
  const end = timestampMillis(league?.bit_tarih);
  return {
    start: Number.isFinite(start) && start > 0 ? turkeyDayStart(start) : -Infinity,
    end: Number.isFinite(end) && end > 0 ? turkeyDayStart(end) + DAY : Infinity,
  };
}

export function getLeagueOfferWeek(league?: OfferWeekLeague | null, now = Date.now()) {
  // Europe/Istanbul has used UTC+03:00 year-round since 2016.
  const local = new Date(now + ISTANBUL_OFFSET);
  const day = local.getUTCDay() || 7;
  const today = Date.UTC(local.getUTCFullYear(), local.getUTCMonth(), local.getUTCDate()) - ISTANBUL_OFFSET;
  const monday = today - (day - 1) * DAY;
  const cutoff = monday + 6 * DAY + 20 * 60 * 60 * 1000;
  const completed = timestampMillis(league?.haftalik_guncelleme_zamani);
  const updateCompleted = completed >= cutoff && completed <= now;
  // Explicit league opt-in can bypass a failed/delayed update, never the
  // Sunday 20:00 boundary or the master early-opening switch.
  const earlyOpen = !!league && league.pazar_aksami_teklif_acik !== false && day === 7 &&
    now >= cutoff && (updateCompleted || league.guncelleme_beklemeden_teklif_acik === true);
  const start = monday + (earlyOpen ? 7 * DAY : 0);
  return {
    start, end: start + 7 * DAY, earlyOpen, openedWithoutUpdate: earlyOpen && !updateCompleted,
    season: leagueSeason(league),
  };
}

export function isInOfferWeek(value: unknown, week: ReturnType<typeof getLeagueOfferWeek>) {
  const time = timestampMillis(value);
  return time >= week.start && time < week.end;
}

/**
 * Maça en az 2 saat kala teklif yapılır; rakibin yanıt süresi kalan sürenin yarısıdır
 * (`offerResponseWindow`), böylece en kısa teklifte bile rakibe 1 saat kalır.
 */
export const OFFER_MIN_LEAD_MS = 2 * 60 * 60 * 1000;

export function isInSeason(value: unknown, league?: OfferWeekLeague | null) {
  const time = timestampMillis(value);
  const season = leagueSeason(league);
  return time >= season.start && time < season.end;
}

/** Teklif haftasında sezon içinde kalan gün yoksa neden: sezon başlamadı ya da bitti. */
export function seasonOfferState(league?: OfferWeekLeague | null, now = Date.now()) {
  const week = getLeagueOfferWeek(league, now);
  const { season } = week;
  if (Math.max(now + OFFER_MIN_LEAD_MS, week.start) >= season.end) return 'ended' as const;
  if (week.end <= season.start) return 'notStarted' as const;
  return null;
}

export function canSelectOfferTime(value: unknown, league?: OfferWeekLeague | null, now = Date.now()) {
  return (
    timestampMillis(value) > now + OFFER_MIN_LEAD_MS &&
    isInOfferWeek(value, getLeagueOfferWeek(league, now)) &&
    isInSeason(value, league)
  );
}

/** A local wall date for date-picker widgets; not a reservation timestamp. */
export function turkeyCalendarDate(time: number): Date {
  const date = new Date(time + ISTANBUL_OFFSET);
  return new Date(date.getUTCFullYear(), date.getUTCMonth(), date.getUTCDate());
}

export function turkeyReservationTime(date: Date, hour: number, minute: number): Date {
  return new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate(), hour, minute) - ISTANBUL_OFFSET);
}

// Sezon dışındaki gün kapalıdır (`outOfSeason`); haftası eski biçimde (sezonsuz) verilirse sınır yok.
export function offerWeekDays(week: Omit<ReturnType<typeof getLeagueOfferWeek>, 'season'> & {
  season?: ReturnType<typeof leagueSeason>;
}, now = Date.now()) {
  const season = week.season || { start: -Infinity, end: Infinity };
  return Array.from({ length: 7 }, (_, index) => {
    const start = week.start + index * DAY;
    const outOfSeason = start + DAY <= season.start || start >= season.end;
    return {
      index: index + 1, start, end: start + DAY, date: turkeyCalendarDate(start),
      disabled: start + DAY <= now || outOfSeason, outOfSeason,
    };
  });
}
